import { FC } from "react";
import { styled } from "@mui/system";
import muiIcon from "../../../components/icons/mui-icons";
import GullTable from "../../../components/tables/GullTable";
import { tableDataList } from "../../../utils/constant-data";
import { H3, Paragraph, Span } from "../../../components/Typography";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { CardContainer } from "../../../components/CardContainer";
import {
  Avatar,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

const Status = styled(Span)<{ status: string }>(({ theme, status }) => ({
  fontSize: 11,
  borderRadius: 5,
  padding: "1px 5px",
  color:
    status === "active"
      ? theme.palette.grey[100]
      : status === "pending" && theme.palette.grey[100],
  background:
    status === "active"
      ? theme.palette.success.main
      : status === "pending"
      ? theme.palette.primary.main
      : status === "inactive" && theme.palette.warning.main,
}));

const Title = styled(H3)(() => ({
  marginBottom: "15px",
}));

const UseCase = styled(Paragraph)(() => ({
  marginBottom: "10px",
  "& span": {
    color: "#cb3066",
    fontSize: 12,
    fontWeight: 600,
  },
}));

const BasicTable: FC = () => {
  const { CloseIcon, EditIcon } = muiIcon;

  return (
    <>
      <Breadcrumb
        name="data-tables"
        subName="Basic Table"
        path="/dashboard/v1"
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} pt="0px">
          <CardContainer>
            <Title>Basic Table</Title>
            <GullTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Basic Dark Table</Title>
            <UseCase>
              Add <Span>tableBg</Span> as a prop on GullTable then pass color
              code or name as a string to make table background.{" "}
              <Span>tHeadTextColor</Span> and <Span>tBodyTextColor</Span>
              both are used to change font color on the table.
            </UseCase>
            <GullTable
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Light Table Head</Title>
            <GullTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Table Head</Title>
            <UseCase>
              Add <Span>tHeadBg</Span> as a prop on GullTable then pass color
              code or name as a string to make table head background and pass
              another pros <Span>tHeadTextColor</Span>
              to change font color on the table head.
            </UseCase>
            <GullTable tHeadTextColor="white" tHeadBg="#47404F">
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Light Hover-able Table</Title>
            <UseCase>
              Add <Span>hover</Span> as a prop on{" "}
              {"GullTable>TableBody>TableRow"}. Then the row will be hover-able.
            </UseCase>
            <GullTable>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow hover id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Hover-able Table</Title>
            <UseCase>
              Add <Span>hover</Span> as a prop on{" "}
              {"GullTable>TableBody>TableRow"}. Then the row will be hover-able.
            </UseCase>
            <GullTable
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow hover id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Light Small Table</Title>
            <UseCase>
              Add prop <Span>tableSize</Span> and pass small or medium or large
              to change table size. Default table size medium.
            </UseCase>
            <GullTable tableSize={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Small Table</Title>
            <UseCase>
              Add <Span>tableSize</Span> as a pros and pass small or medium or
              large to change table size. Default table size medium.
            </UseCase>
            <GullTable
              tableSize={"small"}
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Striped Rows</Title>
            <UseCase>
              Add <Span>oddCellBg</Span> as a prop on GullTable and pass true
              for change background to odd number table cell.
            </UseCase>
            <GullTable oddCellBg={true}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Stripe Rows</Title>
            <UseCase>
              Add <Span>oddCellBg</Span> as a prop on GullTable and pass true
              for change background to odd number table cell.
            </UseCase>
            <GullTable
              oddCellBg={true}
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Bordered Table</Title>
            <UseCase>
              Add <Span>tableBorder</Span> as a prop on GullTable and pass true
              to make table border.
            </UseCase>
            <GullTable tableBorder={true}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Bordered Table</Title>
            <UseCase>
              Add <Span>tableBorder</Span> as a prop on GullTable and pass true
              to make table border.
            </UseCase>
            <GullTable
              tableBorder={true}
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Light Borderless Table</Title>
            <UseCase>
              Add <Span>tableBorder</Span> as a prop on GullTable and pass false
              to remove table border.
            </UseCase>
            <GullTable tableBorder={false}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Borderless Table</Title>
            <UseCase>
              Add <Span>tableBorder</Span> as a prop on GullTable and pass false
              to remove table border.
            </UseCase>
            <GullTable
              tableBorder={false}
              tableBg="#47404F"
              tBodyTextColor="white"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Light Table With Custom Table Cell Color</Title>
            <UseCase>
              Add <Span>oddCellBg</Span> and <Span>evenCellBg</Span> as a prop
              on GullTable and pass true for change background to odd or ever
              number table cell. With that Add <Span>oddCellColor</Span> and{" "}
              <Span>evenCellColor</Span> as a prop and pass color code or color
              name as a string to make background color for even or odd number
              table cell.
            </UseCase>
            <GullTable
              tableBorder={false}
              tHeadBg="#D4C6E2"
              evenCellBg={true}
              evenCellColor="#CDE9CE"
              oddCellBg={true}
              oddCellColor="#FFEEBA"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <CardContainer>
            <Title>Dark Table With Custom Table Cell Color</Title>
            <UseCase>
              Add <Span>oddCellBg</Span> and <Span>evenCellBg</Span> as a prop
              on GullTable and pass true for change background to odd or ever
              number table cell. With that Add <Span>oddCellColor</Span> and{" "}
              <Span>evenCellColor</Span> as a prop and pass color code or color
              name as a string to make background color for even or odd number
              table cell.
            </UseCase>
            <GullTable
              tableBorder={false}
              tHeadBg="#663399"
              evenCellBg={true}
              evenCellColor="#4CAF50"
              oddCellBg={true}
              oddCellColor="#FFC107"
              tHeadTextColor="white"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Avatar</TableCell>
                  <TableCell align="center">Email</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataList.map((user: any, index) => {
                  const { name, photoUrl, email, status } = user;
                  return (
                    <TableRow id="GullTableRow" key={index}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">{name}</TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={photoUrl}
                          alt="Remy Sharp"
                          sx={{ width: 24, height: 24, margin: "auto" }}
                        />
                      </TableCell>
                      <TableCell align="center">{email}</TableCell>
                      <TableCell align="center">
                        <Status status={status}>{status}</Status>
                      </TableCell>
                      <TableCell align="center">
                        <EditIcon
                          sx={{
                            mx: "3px",
                            mb: "-2px",
                            color: "success.main",
                            cursor: "pointer",
                          }}
                        />
                        <CloseIcon
                          sx={{
                            mx: "3px",
                            color: "red",
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </GullTable>
          </CardContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default BasicTable;
